<script setup lang="ts">
import { VarsityHashTabBar, VarsityHashTabGroup, VarsityHashTabLink, VarsityHashTabPage } from "@/components/tabs"
import { generateRandomString } from "@/vf"
import { ref, watch } from "vue"
import FormBuilderCanvas from "./FormBuilderCanvas.vue"
import FormBuilderPalette from "./FormBuilderPalette.vue"
import FormBuilderPreview from "./FormBuilderPreview.vue"
import FormBuilderProperties from "./FormBuilderProperties.vue"
import type { EditorNode } from "./types"
import { useFormBuilder } from "./useFormBuilder"

const props = defineProps<{
    modelValue?: string
}>()

const emit = defineEmits<{
    (e: "update:modelValue", json: string): void
}>()

function parseNodes(json?: string): EditorNode[] {
    if (!json) { return [] }
    try {
        const parsed = JSON.parse(json)
        return Array.isArray(parsed) ? parsed : []
    } catch {
        return []
    }
}

const formBuilder = useFormBuilder(parseNodes(props.modelValue))

const tabGroupName = "form_builder_" + generateRandomString(6)

const jsonText = ref(JSON.stringify(formBuilder.nodes.value, null, 2))

watch(formBuilder.nodes, nodes => {
    emit("update:modelValue", JSON.stringify(nodes))
    jsonText.value = JSON.stringify(nodes, null, 2)
}, { deep: true, immediate: true })

function applyJson() {
    try {
        const parsed = JSON.parse(jsonText.value)
        if (Array.isArray(parsed)) {
            formBuilder.nodes.value = parsed
        }
    } catch {
        // invalid JSON — ignore
    }
}
</script>

<template>
    <div class="form-builder form-group">
        <VarsityHashTabGroup :name="tabGroupName" default="edit">
            <VarsityHashTabBar layout="tabs">
                <VarsityHashTabLink tab="edit">
                    <i class="fa fa-fw fa-edit"></i>
                    {{ $t("components:form_builder.tab_edit") }}
                </VarsityHashTabLink>
                <VarsityHashTabLink tab="preview">
                    <i class="fa fa-fw fa-eye"></i>
                    {{ $t("components:form_builder.tab_preview") }}
                </VarsityHashTabLink>
                <VarsityHashTabLink tab="json">
                    <i class="fa fa-fw fa-code"></i>
                    {{ $t("components:form_builder.tab_source") }}
                </VarsityHashTabLink>
            </VarsityHashTabBar>

            <VarsityHashTabPage tab="edit">
                <div class="fb-layout">
                    <FormBuilderPalette :formBuilder="formBuilder" />
                    <FormBuilderCanvas :formBuilder="formBuilder" />
                    <FormBuilderProperties :formBuilder="formBuilder" />
                </div>
            </VarsityHashTabPage>

            <VarsityHashTabPage tab="preview">
                <FormBuilderPreview :formBuilder="formBuilder" />
            </VarsityHashTabPage>

            <VarsityHashTabPage tab="json">
                <div class="fb-json">
                    <textarea v-model="jsonText" class="fb-json__textarea form-control" spellcheck="false" />
                    <div class="fb-json__actions">
                        <button type="button" class="btn btn-sm btn-primary" @click="applyJson">
                            <i class="fa fa-fw fa-check"></i>
                            {{ $t("components:form_builder.apply") }}
                        </button>
                    </div>
                </div>
            </VarsityHashTabPage>
        </VarsityHashTabGroup>
    </div>
</template>

<style lang="scss" scoped>
.form-builder {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    :deep(.varsity-tab-link) {
        font-size: 1rem;
        padding: 0.75rem 1rem;
    }
}

.fb-layout {
    display: grid;
    grid-template-columns: 170px 1fr 270px;
    gap: 1.25rem;
    align-items: start;
}

.fb-json {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    &__textarea {
        font-family: ui-monospace, "Cascadia Code", "Source Code Pro", monospace;
        font-size: 0.8rem;
        min-height: 300px;
        resize: vertical;
        white-space: pre;
    }

    &__actions {
        display: flex;
        gap: 0.5rem;
    }
}

// Shared field-editing primitives, rendered inside FormBuilderProperties.vue and
// FormBuilderTopicSelector.vue — :deep() so the rules stay scoped to this component's
// DOM subtree instead of leaking globally once FormBuilder.vue is used anywhere.
:deep(.fb-field) {
    margin-bottom: 0.75rem;
}

:deep(.fb-label) {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 0.25rem;
}

:deep(.fb-checkbox) {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin-bottom: 0;
    cursor: pointer;

    input[type="checkbox"] {
        margin: 0;
    }
}

:deep(.fb-hint) {
    font-size: 0.7rem;
    color: #6c757d;
    margin-top: 0.2rem;
}

:deep(.fb-options) {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
}

:deep(.fb-option) {
    display: flex;
    align-items: center;
    gap: 0.25rem;
}

:deep(.fb-option__handle) {
    color: #ced4da;
    cursor: grab;
    padding: 0.2rem 0.3rem;
    flex-shrink: 0;
    font-size: 0.75rem;

    &:active {
        cursor: grabbing;
    }
}
</style>
